import { useMutation, useQuery } from '@apollo/client';
import React, { useContext, useState } from 'react';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import EDList from '../../../../../../../BaseComponents/List/EDList';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import {
  removeIdsFromNewAssociations,
  resolveRefs,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  andraRepAkt,
  andraRepAktVariables,
} from '../../../Aktivitet/Mutations/_QueryTypes/andraRepAkt';
import updateRepeteraAktivitetMutation from '../../../Aktivitet/Mutations/UpdateRepeteradAktivitetMutation.gql';
import { without } from 'ramda';
import { IDefinedBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';
import {
  UpdateRepeteraAktivitetQuery,
  UpdateRepeteraAktivitetQueryVariables,
  UpdateRepeteraAktivitetQuery_node_TEDBOAktivitet__Associations_RepeteradeAktiviteter_All,
} from '../../Queries/_QueryTypes/UpdateRepeteraAktivitetQuery';
import updateRepeteraAktivitetQuery from '../../Queries/UpdateRepeteraAktivitetQuery.gql';
import { MdDetailSelectionContext } from '../../../../../../../Components/MasterDetail/Contexts/MdDetailSelectionContext';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      topArea: {
        justifyContent: 'flex-start',
      },
    } as const)
);

interface IProps {
  formContext: IDefinedBusinessDataFormContext;
  openUpdateRepeteraDialog: boolean;
  setOpenUpdateRepeteraDialog: React.Dispatch<React.SetStateAction<boolean>>;
  activeDatabase: string | null;
  saveAndCloseUpdate: boolean;
  aktivitetId: any;
}

const UpdateRepeteraAktivitet: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();

  const [checkboxes, setCheckboxes] = useState<string[] | undefined>([]);
  const [
    openAllRepeteradeAktiviteterCheckboxes,
    setOpenAllRepeteradeAktiviteterCheckboxes,
  ] = useState(false);
  const [singleAktivitet, setSingleAktivitet] = useState(true);
  const [allAktiviteterChecked, setAllaktiviteterChecked] = useState(false);
  const [whichAktivitet, setWhichAktivitet] = useState<
    null | 'saveSingleAktivitet'
  >('saveSingleAktivitet');
  const isSmall = useIsSmallScreen();
  const values = resolveRefs(props.formContext.formState.values ?? {});
  const newValues = removeIdsFromNewAssociations(values);
  const mdDetailSelectionContext = useContext(MdDetailSelectionContext);
  let filteredAktiviteter:
    | UpdateRepeteraAktivitetQuery_node_TEDBOAktivitet__Associations_RepeteradeAktiviteter_All[]
    | undefined = [];

  const { data: antalRepeteradeAktiviteter_data } = useQuery<
    UpdateRepeteraAktivitetQuery,
    UpdateRepeteraAktivitetQueryVariables
  >(updateRepeteraAktivitetQuery, {
    variables: {
      id: props.aktivitetId.id,
    },
    skip: !props.openUpdateRepeteraDialog || !props.aktivitetId.id,
    onCompleted: () => {
      setCheckboxes(
        filteredAktiviteter?.map((item: { id: any }) => {
          return item.id;
        })
      );
    },
  });

  const [andraRepAkt, { loading: isSaving }] = useMutation<
    andraRepAkt,
    andraRepAktVariables
  >(updateRepeteraAktivitetMutation, {
    refetchQueries: ['AktivitetQuery'],
  });

  const saveSingleAktivitet = () => {
    setSingleAktivitet(!singleAktivitet);
    setOpenAllRepeteradeAktiviteterCheckboxes(false);
    setAllaktiviteterChecked(false);
    if (checkboxes) {
      checkboxes.length = 0;
    }
    if (!singleAktivitet) {
      setWhichAktivitet('saveSingleAktivitet');
    } else {
      setWhichAktivitet(null);
    }
  };

  const NotSingle = () => {
    setWhichAktivitet(null);
    setSingleAktivitet(false);
  };

  const allAktiviteter = () => {
    setAllaktiviteterChecked(!allAktiviteterChecked);
    NotSingle();
    setOpenAllRepeteradeAktiviteterCheckboxes(false);
  };

  const openAllAktivitiesCheckboxes = () => {
    setAllaktiviteterChecked(false);
    setOpenAllRepeteradeAktiviteterCheckboxes(
      !openAllRepeteradeAktiviteterCheckboxes
    );
    NotSingle();
  };

  const closeUpdateRepeteraDialog = () => {
    props.setOpenUpdateRepeteraDialog(false);
  };

  const closeAndSaveUpdateRepeteraDialog = () => {
    if (whichAktivitet === 'saveSingleAktivitet') {
      if (props.saveAndCloseUpdate) {
        props.formContext?.saveForm(
          undefined,
          () => {
            mdDetailSelectionContext.selectDetail?.(null);
          },
          undefined,
          { suppressAddedIdSelection: true }
        );
      } else {
        props.formContext?.saveForm();
      }
      props.setOpenUpdateRepeteraDialog(false);
      props.formContext?.formStateDispatch({ type: 'VALUES_COMMITED' });
    } else {
      andraRepAkt({
        variables: {
          dbname: props.activeDatabase || '',
          updateValues: {
            id: props.formContext?.data?.id ?? '',
            ...newValues,
            _version: props.formContext?.data?._version ?? 1,
          },
          repAktiviteter: checkboxes,
        },
      }).then(result => {
        if (!result.errors && props.saveAndCloseUpdate) {
          mdDetailSelectionContext.selectDetail?.(null);
        }
        props.setOpenUpdateRepeteraDialog(false);
      });
      props.formContext?.formStateDispatch({ type: 'VALUES_COMMITED' });
    }
  };

  if (antalRepeteradeAktiviteter_data?.node?.__typename === 'TEDBOAktivitet') {
    filteredAktiviteter =
      antalRepeteradeAktiviteter_data?.node?._Associations?.RepeteradeAktiviteter_All?.filter(
        x => x.FRANDATUM >= props.formContext?.data?.FRANDATUM
      );
  }

  return (
    <EDDialog
      headerProps={{
        headerIcon: (
          <EDIcon
            marginRight={3}
            color="warning"
            iconType="warning"
            size="large"
          />
        ),
        classes: {
          topArea: classes.topArea,
        } as any,
      }}
      open={props.openUpdateRepeteraDialog}
      onClose={closeUpdateRepeteraDialog}
      title="Repeterad aktivitet"
      footerActions={[
        {
          label: 'Avbryt',
          onClick: closeUpdateRepeteraDialog,
          buttonProps: {
            disabled: isSaving,
          },
        },
        {
          label: 'Spara',
          onClick: closeAndSaveUpdateRepeteraDialog,
          buttonProps: {
            disabledReason: isSaving ? '' : 'Du måste göra minst ett val',
            disabled:
              (!singleAktivitet &&
                !allAktiviteterChecked &&
                checkboxes?.length === 0) ||
              isSaving,
          },
        },
      ]}
      data-testid="UpdateRepeteradAktivitetDialog"
    >
      <EDBox sx={{ position: isSmall ? 'static' : 'relative' }}>
        {isSaving && <EDAvenyProgress overlay />}
        {antalRepeteradeAktiviteter_data?.node?.__typename ===
          'TEDBOAktivitet' && (
          <>
            <EDTypography bold>
              Denna aktivitet ingår i en repetition. Vill du genomföra ändringar
              i:
            </EDTypography>
            <EDList>
              <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                <EDCheckbox
                  checked={singleAktivitet}
                  onCheck={saveSingleAktivitet}
                />

                <EDTypography>Endast denna aktivitet</EDTypography>
              </EDBox>

              <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                <EDCheckbox
                  checked={allAktiviteterChecked}
                  onCheck={() => {
                    if (
                      antalRepeteradeAktiviteter_data?.node?.__typename ===
                      'TEDBOAktivitet'
                    ) {
                      setCheckboxes(
                        filteredAktiviteter?.map((item: { id: any }) => {
                          return item.id;
                        })
                      );
                      allAktiviteter();
                    } else {
                      () => {};
                    }
                  }}
                />
                <EDTypography>
                  Alla framtida aktiviteter (
                  {
                    filteredAktiviteter?.filter(
                      (x: any) =>
                        x.FRANDATUM > props.formContext?.data?.FRANDATUM
                    )?.length
                  }
                  )
                </EDTypography>
              </EDBox>

              <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                <EDCheckbox
                  checked={openAllRepeteradeAktiviteterCheckboxes}
                  onCheck={openAllAktivitiesCheckboxes}
                />

                <EDTypography>
                  Välj vilka andra aktiviteter som ska ändras
                </EDTypography>
              </EDBox>

              {openAllRepeteradeAktiviteterCheckboxes && (
                <EDBox sx={{ marginLeft: 2 }}>
                  <EDBox sx={{ display: 'flex', alignItems: 'center' }}></EDBox>
                  {filteredAktiviteter?.map((item: any) => {
                    if (item.id === props.formContext?.data?.id) {
                      return null;
                    } else if (isSmall) {
                      return (
                        <EDBox
                          key={item.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginLeft: 2,
                          }}
                        >
                          <EDCheckbox
                            checked={checkboxes?.includes(item.id)}
                            onCheck={checked => {
                              if (checked) {
                                setCheckboxes(prevValue => {
                                  if (prevValue) {
                                    return [...prevValue, item.id];
                                  } else {
                                    return [];
                                  }
                                });
                              } else {
                                setCheckboxes(prevValue => {
                                  if (prevValue) {
                                    return without([item.id], prevValue);
                                  } else {
                                    return [];
                                  }
                                });
                              }
                            }}
                          />
                          <EDTypography sx={{ marginRight: '5px' }} bold>
                            {item.TITEL},
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.BOKNINGSNUMMER},
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            plats
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.FRANDATUM}, {item.FRANTID} -
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.TILLDATUM}, {item.TILLTID}
                          </EDTypography>
                        </EDBox>
                      );
                    } else {
                      return (
                        <EDBox
                          key={item.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 2,
                          }}
                        >
                          <EDCheckbox
                            checked={checkboxes?.includes(item.id)}
                            onCheck={checked => {
                              if (checked) {
                                setCheckboxes(prevValue => {
                                  if (prevValue) {
                                    return [...prevValue, item.id];
                                  } else {
                                    return [];
                                  }
                                });
                              } else {
                                setCheckboxes(prevValue => {
                                  if (prevValue) {
                                    return without([item.id], prevValue);
                                  } else {
                                    return [];
                                  }
                                });
                              }
                            }}
                          />
                          <EDTypography sx={{ marginRight: '5px' }} bold>
                            {item.TITEL},
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.BOKNINGSNUMMER},
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            plats
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.FRANDATUM}, {item.FRANTID} -
                          </EDTypography>
                          <EDTypography sx={{ marginRight: '5px' }}>
                            {item.TILLDATUM}, {item.TILLTID}
                          </EDTypography>
                        </EDBox>
                      );
                    }
                  })}
                </EDBox>
              )}
            </EDList>
          </>
        )}
      </EDBox>
    </EDDialog>
  );
};

export default UpdateRepeteraAktivitet;
